import { styled } from "@mui/material";
import { Switch, SwitchProps } from "@mui/material";
import { Stack } from "@mui/material";
import Typography, { ITypography } from "../typography/typography";
import { useLanguage } from '@erpsquad/common/runtime';
import { memo } from "react";

export interface ISwitchProps extends SwitchProps {
  label?: string;
  labelPosition?: "start" | "end";
  typographyProps?: ITypography;
}

const CustomisedSwitch = styled((props: ISwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme, size, checked }) => ({
  width: 24,
  height: 16,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: size === "medium" ? 2 : 1,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: size === "medium" ? "translateX(16px)" : "translateX(8px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 14,
    height: 14,
    border: checked ? 0 : `1px solid ${theme.palette.theme?.secondary[300]}`,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.theme?.secondary[200],
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
  ...(size === "medium" && {
    width: 42,
    height: 26,
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 22,
      height: 22,
      border: checked ? 0 : `1px solid ${theme.palette.theme?.secondary[300]}`,
    },
  }),
}));

const ToggleSwitch: React.FC<ISwitchProps> = memo(({
  label,
  labelPosition = "end",
  typographyProps,
  ...rest
}) => {
  const { isRtl } = useLanguage();
  return (
    <Stack direction="row" spacing={isRtl ? '' : 0.5} alignItems="center">
      {labelPosition === "start" && (
        <Typography type="s3" color="theme.secondary.1000" {...typographyProps}>
          {label}
        </Typography>
      )}
      <CustomisedSwitch sx={{ m: 1 }} {...rest} />
      {labelPosition === "end" && (
        <Typography type="s3" color="theme.secondary.1000" {...typographyProps}>
          {label}
        </Typography>
      )}
    </Stack>
  );
});

export default ToggleSwitch;
